<template>
    <Dialog v-model:visible="internalValue" modal :closable="true" class="new-agreement-dialog"
            :header="$t('message.horseEdit.covering.dialogAgreementHeader') + ' ' + horseName"
            @hide="() => {internalRenew = false; internalRowData = null}">
        <div class="flex flex-column gap-5">
            <div class="flex flex-column gap-3 w-full align-items-center">
                <CustomSelectOneSeason name="season" v-model="newAgreement.seasonId" :label="$t('message.horseEdit.covering.season')"
                                class="w-8" noPlaceholder required
                                :customErrorMessage="getRequiredMessage(newAgreement.season)"/>
                <CustomSelectOneAgreementType name="agreementType" :label="$t('message.horseEdit.covering.agreementType')"
                                              class="w-8" noPlaceholder v-model="newAgreement.type" required
                                              :customErrorMessage="getRequiredMessage(newAgreement.type)"
                                              :disabled="internalRenew || (lastCoveringAgreementType === 'PERMANENT' && !isAdmin)"/>
                <CustomSelectOneCity name="location" :label="$t('message.horseEdit.covering.location')"
                                     class="w-8" noPlaceholder v-model="newAgreement.location" required
                                     :customErrorMessage="getRequiredMessage(newAgreement.location)"
                                     :disabled="internalRenew" filter />
                <div class="flex gap-2 w-8 certificate-box">
                    <CustomInputText name="certificateNumberFrom" :label="$t('message.horseEdit.covering.certificateNum')"
                                     class="w-6" noPlaceholder v-model="newAgreement.agreementNumberMin" required
                                     :customErrorMessage="getRequiredMessage(newAgreement.agreementNumberMin)"
                                     @input="inputFormat('agreementNumberMin')"/>
                    <CustomInputText name="certificateNumbersTo" class="w-6" noPlaceholder
                                     v-model="newAgreement.agreementNumberMax" required
                                     :customErrorMessage="getRequiredMessage(newAgreement.agreementNumberMax)"
                                     @input="inputFormat('agreementNumberMax')"/>
                </div>
                <CustomInputTextArea name="notes" :label="$t('message.horseEdit.covering.notes')" v-model="newAgreement.notes" cols="27" />
            </div>
            <div class="flex justify-content-center">
                <CustomButton :label="$t('message.horseEdit.covering.addAgreement')" @click="saveAgreement" />
            </div>
        </div>
    </Dialog>
</template>

<script>
    import _ from "lodash"
    import Dialog from 'primevue/dialog';
    import CustomCalendar from '@/components/form/CustomCalendar.vue';
    import CustomInputNumber from '@/components/form/CustomInputNumber.vue';
    import CustomInputText from '@/components/form/CustomInputText.vue';
    import CustomSelectOne from '@/components/form/inner/CustomSelectOne.vue';
    import CustomButton from '@/components/form/CustomButton.vue';
    import {
        createCoveringAgreement,
    } from "@/swagger/vue-api-client";
    import CustomSelectOneAgreementType from '@/components/form/CustomSelectOneAgreementType.vue';
    import { ValidateUtils } from '@/utils/ValidateUtils';
    import CustomInputTextArea from '@/components/form/CustomInputTextArea.vue';
    import { DateUtils } from '@/utils/DateUtils';
    import CustomSelectOneSeason from "@/components/form/CustomSelectOneSeason.vue";
    import CustomSelectOneCity from "@/components/form/CustomSelectOneCity.vue";
    import {SystemRole} from "@/utils/SystemRole";

    export default {
        name: "NewAgreementDialog",

        components: {
            CustomSelectOneSeason,
            CustomSelectOne,
            CustomInputText,
            CustomCalendar,
            CustomInputNumber,
            Dialog,
            CustomButton,
            CustomSelectOneAgreementType,
            CustomInputTextArea,
            CustomSelectOneCity,
        },

        props: {
            modelValue: null,
            horseName: {
                type: String,
                default: "",
            },
            renew: {
                type: Boolean,
                default: false,
            },
            rowData: null,
            lastCoveringAgreementType: null,
        },

        emits: ["search", "update:renew", "update:modelValue", "update:rowData"],

        data() {
            return {
                newAgreement: this.getClearNewAgreement(),
                showErrorMessages: false,
                systemRole: SystemRole.getUserRole(),
            };
        },

        methods: {
            inputFormat(inputValue) {
                let value = this.newAgreement[inputValue].replace(/^0+/, '');

                if (value.length < 6) {
                    value = value.padStart(6, '0');
                }

                if (value.length > 6) {
                    value = value.slice(0, 6);
                }

                this.newAgreement[inputValue] = value;
            },
            validateData() {
                let isCorrect = ValidateUtils.validateNotEmpty(this.newAgreement, ["stallionId", "type", "location", "agreementNumberMin", "agreementNumberMax", "seasonId"]);
                this.showErrorMessages = !isCorrect;
                return isCorrect;
            },
            saveAgreement() {
                const newAgreementRequest = _.cloneDeep(this.newAgreement);
                if (this.validateData()) {
                    createCoveringAgreement({body: newAgreementRequest})
                        .then(() => {
                            this.$emit("search");
                            this.newAgreement = this.getClearNewAgreement();
                            this.internalValue = false;
                            if (this.internalRenew) {
                                this.internalRenew = false;
                                this.internalRowData = null;
                            }
                            this.notifySaved();
                        })
                        .catch((err) => {
                            this.notifyError(err);
                        });
                }
            },
            getRequiredMessage(value) {
                if (this.showErrorMessages && (value === null || value === "")) {
                    return this.$t("message.validations.required");
                }
                return "";
            },
            getClearNewAgreement() {
                return {
                    stallionId: this.$route.params.id,
                    type: null,
                    location: null,
                    agreementNumberMin: null,
                    agreementNumberMax: null,
                    notes: null,
                    seasonId: null,
                };
            },
            notifySaved() {
                this.$toast.add({
                    severity: "success",
                    summary: this.$t('message.other.success'),
                    detail: this.$t('message.other.dataSaved'),
                    life: 3000,
                });
            },
            notifyError(error) {
                const message = error.response?.data?.message || this.$t('unknownError');
                this.$toast.add({
                    severity: "error",
                    summary: this.$t('message.other.error'),
                    detail: message,
                    life: 12000,
                });
            },
        },

        computed: {
            internalValue: {
                get() {
                    return this.modelValue;
                },
                set(value) {
                    this.$emit("update:modelValue", value);
                },
            },
            internalRenew: {
                get() {
                    return this.renew;
                },
                set(value) {
                    this.$emit("update:renew", value);
                },
            },
            internalRowData: {
                get() {
                    return this.rowData;
                },
                set(value) {
                    this.$emit("update:rowData", value);
                },
            },
            isAdmin() {
                return this.systemRole === SystemRole.ADMIN;
            },
        },

        watch: {
            internalRenew(value) {
                if (value) {
                    this.newAgreement = {
                        ...this.newAgreement,
                        type: this.internalRowData.type,
                        location: this.internalRowData.location,
                        id: this.internalRowData.id,
                    };
                } else {
                    this.newAgreement = this.getClearNewAgreement();
                }
            },
            lastCoveringAgreementType(value) {
                if (value === "PERMANENT" && !this.isAdmin) {
                    this.newAgreement.type = value;
                }
            },
        },
    };
</script>

<style lang="scss">
    .new-agreement-dialog {
        .custom-input-text, .custom-input-number, .custom-select-one, .custom-calendar, .custom-input-textarea {
            padding-left: 170px;
            .custom-label {
                z-index: 999;
                position: absolute;
                transform: translateX(-100%);
                left: 160px;
                text-align: right;
            }
        }
        &.p-dialog {
            .p-dialog-header {
                justify-content: space-between;
                .p-dialog-title {
                    color: var(--accent-color);
                    font-family: var(--decor-font-family);
                    font-weight: 500;
                    font-size: 25px;
                }
            }
            .p-dialog-content {
                padding: 1.5rem;
            }
        }
        .certificate-box {
            .custom-input-text:nth-of-type(1) {
                padding-left: 100px;
                right: -70px;
                .custom-label {
                    left: 90px;
                }
            }
            .custom-input-text:nth-of-type(2) {
                padding-left: 100px;
            }
        }
    }
</style>
